import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Chip from "@/components/ui/chip";
import Popover from "@/components/ui/popover";
import InfoCard from "@/components/ui/info-card";
import {
  Actions as ActionsIcon,
  Eye as EyeIcon,
  Download as DownloadIcon,
  Logs as LogsIcon,
  // Başarılı: yalın çek işareti (Check) — AlertSquareRounded (Başarısız) ile
  // karışmasın diye rounded-square yerine ince stroke tick. Hata icon'u zaten
  // üçgen/çarpı formu — üç ikon birbirinden farklı görünüyor.
  Check as SuccessIcon,
  AlertSquareRounded as AlertIcon,
  Error as ErrorIcon,
} from "@/components/icons";
import type { FilterField, TableColumn, SortOption } from "@/types/component";
import type { AuditLog } from "../types/logs.types";
import { logsApi, formatLogDateTime, toAuditLogLabel } from "../services/logs.api";
import { humanizeBackendError } from "../utils/errorMessageMap";
import LogDetailDrawer from "../components/LogDetailDrawer";
import styles from "./LogsPage.module.scss";

const SEARCH_DEBOUNCE_MS = 400;

/**
 * Göreli zaman — "3 dk önce", "2 sa önce", "4 gün önce", aksi halde boş.
 * 7+ güne çıkınca mutlak tarih yeterli olduğu için boş döner.
 */
function formatRelative(iso: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  if (!iso) return "";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "";
  const diff = Math.max(0, Date.now() - ts);
  const s = Math.floor(diff / 1000);
  if (s < 60) return t("logs.relativeTime.secondsAgo", { n: s });
  const m = Math.floor(s / 60);
  if (m < 60) return t("logs.relativeTime.minutesAgo", { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("logs.relativeTime.hoursAgo", { n: h });
  const d = Math.floor(h / 24);
  if (d < 7) return t("logs.relativeTime.daysAgo", { n: d });
  return "";
}

/** Kullanıcı avatarı için baş harfler. */
function initialsOf(name: string | undefined | null): string {
  if (!name) return "—";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ACTION_CHIP_TYPES: Record<
  string,
  "success" | "warning" | "error" | "info" | "default"
> = {
  CREATE: "success",
  UPDATE: "info",
  DELETE: "error",
  VIEW: "warning",
  EXPORT: "info",
  LOGIN: "warning",
  LOGOUT: "default",
  SECURITY: "default",
};

function getActionChip(action: string, t: (k: string) => string) {
  const key = String(action).toUpperCase();
  const type = ACTION_CHIP_TYPES[key] ?? "default";
  const i18nKey = `logs.actionLabels.${key}`;
  const label = t(i18nKey);
  return {
    label: label !== i18nKey ? label : action || "—",
    type,
  };
}

/**
 * Endpoint path'inden tanınan örüntüleri kullanıcı açıklamasına çevirir.
 * Eşleşen pattern yoksa null döner, caller generic entity+action akışına düşer.
 */
function describeFromPath(
  path: string | null | undefined,
  method: string | null | undefined,
  t: (k: string) => string,
): string | null {
  if (!path) return null;
  const p = path.toLowerCase();
  const m = String(method || "").toUpperCase();

  // Yetki / profil
  if (p.endsWith("/permissions/users/me/permissions"))
    return t("logs.descriptions.permissionCheck");
  if (/\/permissions\/users\/[^/]+\/permissions$/.test(p))
    return m === "GET"
      ? t("logs.descriptions.userPermissionsView")
      : t("logs.descriptions.userPermissionsUpdate");
  if (/\/permissions\/roles\/[^/]+\/permissions$/.test(p))
    return m === "GET"
      ? t("logs.descriptions.rolePermissionsView")
      : t("logs.descriptions.rolePermissionsUpdate");
  if (p.endsWith("/users/me") || p.endsWith("/users/me/profile"))
    return m === "GET"
      ? t("logs.descriptions.profileView")
      : t("logs.descriptions.profileUpdate");

  // Kimlik doğrulama
  if (p.endsWith("/auth/login") || p.endsWith("/auth/login/ldap-local"))
    return t("logs.descriptions.loginSuccess");
  if (p.endsWith("/auth/logout")) return t("logs.descriptions.logoutSuccess");
  if (p.endsWith("/auth/refresh")) return t("logs.descriptions.tokenRefreshed");
  // Eski kayıtlar için tutuluyor; uçlar artık feature flag ile kapalı (pentest 3.3.3).
  if (
    p.endsWith("/auth/forgot-password") ||
    p.endsWith("/auth/forgot-password-email")
  )
    return t("logs.descriptions.forgotPasswordRequested");
  if (
    p.endsWith("/auth/reset-password") ||
    p.endsWith("/auth/reset-password-email")
  )
    return t("logs.descriptions.passwordReset");
  if (p.includes("/auth/otp")) return t("logs.descriptions.otpVerified");

  // Bildirim / cihaz tokenları — users/me alt yolları dahil
  if (p.endsWith("/users/me/notifications/unread-count"))
    return t("logs.descriptions.unreadNotificationsCount");
  if (p.endsWith("/users/me/notifications/mark-all-read"))
    return t("logs.descriptions.allNotificationsMarkedRead");
  if (p.endsWith("/users/me/notifications"))
    return t("logs.descriptions.notificationsView");
  if (/\/users\/me\/notifications\/[^/]+\/mark-read$/.test(p))
    return t("logs.descriptions.notificationMarkedRead");
  if (/\/users\/me\/notifications\/[^/]+$/.test(p) && m === "DELETE")
    return t("logs.descriptions.notificationDeleted");
  if (p.endsWith("/users/me/notification-preferences"))
    return m === "GET"
      ? t("logs.descriptions.notificationPreferencesView")
      : t("logs.descriptions.notificationPreferencesUpdate");
  if (p.endsWith("/users/me/device-tokens") || p.endsWith("/device-tokens"))
    return m === "POST"
      ? t("logs.descriptions.deviceTokenSaved")
      : t("logs.descriptions.deviceTokensView");
  if (/\/device-tokens\/[^/]+$/.test(p) && m === "DELETE")
    return t("logs.descriptions.deviceTokenDeleted");
  if (p.includes("/notifications/") && p.endsWith("/mark-read"))
    return t("logs.descriptions.notificationMarkedRead");

  // Export
  if (p.endsWith("/export") || p.includes("/export?"))
    return t("logs.descriptions.exported");

  return null;
}

/** AUTH/LOGIN logları için backend metadata'sından girilen kullanıcı adını çek. */
function getAttemptedUsername(log: AuditLog): string {
  const meta = log.metadata as Record<string, unknown> | null | undefined;
  const u = meta && typeof meta === "object" ? meta["attemptedUsername"] : null;
  return typeof u === "string" && u.trim() ? u.trim() : "";
}

function isFailedStatus(log: AuditLog): boolean {
  const raw = String(log.status ?? "").toUpperCase();
  if (raw === "FAILED" || raw === "ERROR") return true;
  const code = log.statusCode ?? 0;
  return code >= 400;
}

/**
 * Kullanıcıya gösterilecek özet cümle.
 *
 * Sıralama:
 * 1. AUTH/LOGIN — status'a göre adlandırılmış cümle (kullanıcı adı görünür)
 * 2. action LOGOUT
 * 3. FAILED/ERROR — humanizeBackendError ile sade Türkçe (ham mesaj sızmaz)
 * 4. describeFromPath — başarı senaryosu için tanınan endpoint cümleleri
 * 5. entity + verb fallback
 */
function buildLogDescription(
  log: AuditLog,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  const action = String(log.action ?? "").toUpperCase();

  if (action === "LOGIN") {
    const username = getAttemptedUsername(log) || log.userDisplayName || "—";
    if (isFailedStatus(log)) {
      return t("logs.descriptions.loginFailed", { username });
    }
    return username !== "—"
      ? t("logs.descriptions.loginSuccessNamed", { username })
      : t("logs.descriptions.loginSuccess");
  }

  if (action === "LOGOUT") return t("logs.descriptions.logoutSuccess");

  if (isFailedStatus(log)) {
    return humanizeBackendError(log.errorMessage, log.statusCode, t);
  }

  // Endpoint-based cümle — entity fallback'ten önce denenir, böylece ham
  // "/api/..." path'leri kullanıcıya sızmaz.
  const fromPath = describeFromPath(log.requestPath, log.requestMethod, t);
  if (fromPath) return fromPath;

  const rawEntity = (log.entityType ?? log.module ?? "").trim();
  const entity = rawEntity ? toAuditLogLabel(rawEntity) : "";
  const verbKey = `logs.verbs.${action}`;
  const verbLabel = t(verbKey);
  const verb = verbLabel !== verbKey ? verbLabel : "";
  if (entity && verb) return t("logs.descriptions.entityVerb", { entity, verb });
  if (verb) return t("logs.descriptions.recordVerb", { verb });
  return log.endpointName || "—";
}

/**
 * Status rozeti — backend `status` önceliklidir; yoksa HTTP status koduna
 * göre: 2xx Başarılı, 4xx Başarısız, 5xx Hata.
 */
function getStatusChip(
  log: AuditLog,
  t: (k: string) => string,
): {
  label: string;
  type: "success" | "warning" | "error" | "default";
} {
  const raw = String(log.status ?? "").toUpperCase();
  if (raw === "SUCCESS")
    return { label: t("logs.statusLabels.success"), type: "success" };
  if (raw === "FAILED")
    return { label: t("logs.statusLabels.failed"), type: "warning" };
  if (raw === "ERROR")
    return { label: t("logs.statusLabels.error"), type: "error" };
  const code = log.statusCode ?? 0;
  if (code >= 200 && code < 300)
    return { label: t("logs.statusLabels.success"), type: "success" };
  if (code >= 400 && code < 500)
    return { label: t("logs.statusLabels.failed"), type: "warning" };
  if (code >= 500)
    return { label: t("logs.statusLabels.error"), type: "error" };
  return { label: "—", type: "default" };
}

const LogsPage: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<
    (AuditLog & { dateTimeFormatted?: string })[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [sortBy, setSortBy] = useState<
    "createdAt" | "statusCode" | "duration" | "user" | "module" | "action"
  >("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [statusCounts, setStatusCounts] = useState<{
    total: number;
    success: number;
    failed: number;
    error: number;
  }>({ total: 0, success: 0, failed: 0, error: 0 });

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const moduleFilter = filterValues.module;
      const actionFilter = filterValues.action;
      const statusFilter = filterValues.status;
      const startDate = filterValues.startDate as string | undefined;
      const endDate = filterValues.endDate as string | undefined;
      const entityType = filterValues.entityType as string | undefined;
      const userID = filterValues.userID as string | undefined;

      const response = await logsApi.getAll({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        module: Array.isArray(moduleFilter)
          ? (moduleFilter[0] as string)
          : (moduleFilter as string),
        action: Array.isArray(actionFilter)
          ? (actionFilter[0] as string)
          : (actionFilter as string),
        status: statusFilter as "SUCCESS" | "FAILED" | "ERROR" | undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        entityType: entityType || undefined,
        userID: userID || undefined,
        sortBy,
        sortOrder,
      });
      const withFormatted = (response.data ?? []).map((log) => ({
        ...log,
        dateTimeFormatted: formatLogDateTime(log.createdAt),
      }));
      setLogs(withFormatted);
      setTotalRecords(response.total);
    } catch (error) {
      console.error("Error fetching logs:", error);
      setLogs([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, filterValues, sortBy, sortOrder]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /**
   * Stat kartları için 4 paralel count sorgusu — status hariç aynı filtre
   * setini kullanır, böylece kullanıcı search/module/date-range değiştirince
   * sayılar canlı güncellenir. `limit=1` ile sadece count alanını ediniyoruz.
   */
  useEffect(() => {
    let cancelled = false;
    const moduleFilter = filterValues.module;
    const actionFilter = filterValues.action;
    const startDate = filterValues.startDate as string | undefined;
    const endDate = filterValues.endDate as string | undefined;
    const entityType = filterValues.entityType as string | undefined;
    const userID = filterValues.userID as string | undefined;
    const base = {
      page: 1,
      limit: 1,
      search: searchTerm || undefined,
      module: Array.isArray(moduleFilter)
        ? (moduleFilter[0] as string)
        : (moduleFilter as string),
      action: Array.isArray(actionFilter)
        ? (actionFilter[0] as string)
        : (actionFilter as string),
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      entityType: entityType || undefined,
      userID: userID || undefined,
    } as const;
    Promise.all([
      logsApi.getAll(base),
      logsApi.getAll({ ...base, status: "SUCCESS" }),
      logsApi.getAll({ ...base, status: "FAILED" }),
      logsApi.getAll({ ...base, status: "ERROR" }),
    ])
      .then(([total, success, failed, err]) => {
        if (cancelled) return;
        setStatusCounts({
          total: total.total,
          success: success.total,
          failed: failed.total,
          error: err.total,
        });
      })
      .catch(() => {
        /* stats başarısız olsa bile tabloyu etkilemiyor */
      });
    return () => {
      cancelled = true;
    };
  }, [
    searchTerm,
    filterValues.module,
    filterValues.action,
    filterValues.startDate,
    filterValues.endDate,
    filterValues.entityType,
    filterValues.userID,
  ]);

  const handleSearch = (value: string) => setSearchInput(value);

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleDownload = async () => {
    const moduleFilter = filterValues.module;
    const actionFilter = filterValues.action;
    const statusFilter = filterValues.status;
    const startDate = filterValues.startDate as string | undefined;
    const endDate = filterValues.endDate as string | undefined;
    const entityType = filterValues.entityType as string | undefined;
    const userID = filterValues.userID as string | undefined;

    setIsExporting(true);
    try {
      const { blob, filename } = await logsApi.export({
        page: 1,
        limit: 10000,
        search: searchTerm || undefined,
        module: Array.isArray(moduleFilter)
          ? (moduleFilter[0] as string)
          : (moduleFilter as string),
        action: Array.isArray(actionFilter)
          ? (actionFilter[0] as string)
          : (actionFilter as string),
        status: statusFilter as "SUCCESS" | "FAILED" | "ERROR" | undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        entityType: entityType || undefined,
        userID: userID || undefined,
        sortBy,
        sortOrder,
        format: "csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  const handleSortChange = (option: SortOption) => {
    const combined = String(option.value ?? "");
    const [field, dir] = combined.split("-");
    setSortBy((field as typeof sortBy) || "createdAt");
    setSortOrder((dir as "asc" | "desc") || "desc");
    setCurrentPage(1);
  };

  /**
   * Stat kartına tıklama — status filtresini toggle eder. Aynı karta tekrar
   * basınca filtre kalkar ("tümü"ne döner). Advanced filter panelindeki
   * status dropdown ile aynı `filterValues.status`'u kullandığı için iki
   * giriş noktası da senkron kalıyor.
   */
  const activeStatus = (filterValues.status as string | undefined) ?? null;
  const handleStatusCardClick = (status: "SUCCESS" | "FAILED" | "ERROR" | null) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      if (status === null || prev.status === status) {
        delete next.status;
      } else {
        next.status = status;
      }
      return next;
    });
    setCurrentPage(1);
  };

  const filterFields: FilterField[] = [
    {
      name: "action",
      label: t("logs.filters.operationType"),
      type: "dropdown",
      placeholder: t("logs.filters.operationTypePlaceholder"),
      options: [
        { label: t("logs.actionLabels.CREATE"), value: "CREATE" },
        { label: t("logs.actionLabels.UPDATE"), value: "UPDATE" },
        { label: t("logs.actionLabels.DELETE"), value: "DELETE" },
        { label: t("logs.actionLabels.VIEW"), value: "VIEW" },
        { label: t("logs.actionLabels.EXPORT"), value: "EXPORT" },
        { label: t("logs.actionLabels.LOGIN"), value: "LOGIN" },
        { label: t("logs.actionLabels.SECURITY"), value: "SECURITY" },
      ],
      searchable: true,
      multiple: false,
    },
    {
      name: "module",
      label: t("logs.filters.module"),
      type: "dropdown",
      placeholder: t("logs.filters.modulePlaceholder"),
      options: [
        { label: t("logs.moduleLabels.USERS"), value: "USERS" },
        { label: t("logs.moduleLabels.ROLES"), value: "ROLES" },
        { label: t("logs.moduleLabels.AUTH"), value: "AUTH" },
        { label: t("logs.moduleLabels.DEPARTMENTS"), value: "DEPARTMENTS" },
        { label: t("logs.moduleLabels.EDUCATIONS"), value: "EDUCATIONS" },
        { label: t("logs.moduleLabels.EDUCATION_CATEGORIES"), value: "EDUCATION_CATEGORIES" },
        { label: t("logs.moduleLabels.EXAMS"), value: "EXAMS" },
        { label: t("logs.moduleLabels.SURVEYS"), value: "SURVEYS" },
        { label: t("logs.moduleLabels.CERTIFICATES"), value: "CERTIFICATES" },
        { label: t("logs.moduleLabels.LIBRARY"), value: "LIBRARY" },
        { label: t("logs.moduleLabels.ANNOUNCEMENTS"), value: "ANNOUNCEMENTS" },
        { label: t("logs.moduleLabels.SUPPORT_TICKETS"), value: "SUPPORT_TICKETS" },
        { label: t("logs.moduleLabels.NOTIFICATIONS"), value: "NOTIFICATIONS" },
        { label: t("logs.moduleLabels.LOCATIONS"), value: "LOCATIONS" },
        { label: t("logs.moduleLabels.COMPANIES"), value: "COMPANIES" },
        { label: t("logs.moduleLabels.EMAIL_BROADCAST"), value: "EMAIL_BROADCAST" },
        { label: t("logs.moduleLabels.SMS_BROADCAST"), value: "SMS_BROADCAST" },
        { label: t("logs.moduleLabels.INTERNS"), value: "INTERNS" },
        { label: t("logs.moduleLabels.REPORTS"), value: "REPORTS" },
        { label: t("logs.moduleLabels.DOMAIN_SETTINGS"), value: "DOMAIN_SETTINGS" },
        { label: t("logs.moduleLabels.AUDIT_LOGS"), value: "AUDIT_LOGS" },
      ],
      searchable: true,
      multiple: false,
    },
    {
      name: "status",
      label: t("logs.filters.status"),
      type: "dropdown",
      placeholder: t("logs.filters.statusPlaceholder"),
      options: [
        { label: t("logs.statusLabels.success"), value: "SUCCESS" },
        { label: t("logs.statusLabels.failed"), value: "FAILED" },
        { label: t("logs.statusLabels.error"), value: "ERROR" },
      ],
      multiple: false,
    },
    {
      name: "entityType",
      label: t("logs.filters.entityType"),
      type: "dropdown",
      placeholder: t("logs.filters.entityTypePlaceholder"),
      options: [
        { label: t("logs.entityLabels.USER"), value: "User" },
        { label: t("logs.entityLabels.ROLE"), value: "Role" },
        { label: t("logs.entityLabels.PERMISSION"), value: "Permission" },
        { label: t("logs.entityLabels.EDUCATION"), value: "Education" },
        { label: t("logs.entityLabels.EDUCATIONCATEGORY"), value: "EducationCategory" },
        { label: t("logs.entityLabels.EDUCATIONENROLLMENT"), value: "EducationEnrollment" },
        { label: t("logs.entityLabels.EXAM"), value: "Exam" },
        { label: t("logs.entityLabels.EXAMATTEMPT"), value: "ExamAttempt" },
        { label: t("logs.entityLabels.SURVEY"), value: "Survey" },
        { label: t("logs.entityLabels.SURVEYANSWER"), value: "SurveyAnswer" },
        { label: t("logs.entityLabels.CERTIFICATE"), value: "Certificate" },
        { label: t("logs.entityLabels.ANNOUNCEMENT"), value: "Announcement" },
        { label: t("logs.entityLabels.SUPPORTTICKET"), value: "SupportTicket" },
        { label: t("logs.entityLabels.DEPARTMENT"), value: "Department" },
        { label: t("logs.entityLabels.INTERN"), value: "Intern" },
        { label: t("logs.entityLabels.NOTIFICATION"), value: "Notification" },
      ],
      searchable: true,
      multiple: false,
    },
    {
      name: "startDate",
      label: t("logs.filters.startDate"),
      type: "date",
      placeholder: "YYYY-MM-DD",
    },
    {
      name: "endDate",
      label: t("logs.filters.endDate"),
      type: "date",
      placeholder: "YYYY-MM-DD",
    },
  ];

  const sortByOptions: SortOption[] = [
    { label: t("logs.sort.dateNewest"), value: "createdAt", direction: "desc" },
    { label: t("logs.sort.dateOldest"), value: "createdAt", direction: "asc" },
    { label: t("logs.sort.module"), value: "module", direction: "asc" },
    { label: t("logs.sort.operation"), value: "action", direction: "asc" },
    { label: t("logs.sort.statusCode"), value: "statusCode", direction: "desc" },
  ];

  const columns: TableColumn<AuditLog & { dateTimeFormatted?: string }>[] = [
    {
      header: t("logs.columns.dateTime"),
      accessor: (row) => (
        <div className={styles.cellDate}>
          <span className={styles.cellDatePrimary}>
            {row.dateTimeFormatted ?? row.createdAt}
          </span>
          <span className={styles.cellDateRelative}>
            {formatRelative(row.createdAt, t)}
          </span>
        </div>
      ),
      className: "logs-date-column",
    },
    {
      header: t("logs.columns.user"),
      accessor: (row) => (
        <div className={styles.cellUser} title={row.userDisplayName}>
          <span className={styles.cellAvatar} aria-hidden>
            {initialsOf(row.userDisplayName)}
          </span>
          <span className={styles.cellUserName}>
            {row.userDisplayName || "—"}
          </span>
        </div>
      ),
      className: "logs-user-column",
    },
    {
      header: t("logs.columns.operation"),
      accessor: (row) => {
        const chip = getActionChip(row.action, t);
        return (
          <div className={styles.cellOperation}>
            <Chip type={chip.type} variant="soft" size="small">
              {chip.label}
            </Chip>
            <span className={styles.cellOperationModule}>
              {toAuditLogLabel(row.module)}
            </span>
          </div>
        );
      },
      className: "logs-operation-column",
    },
    {
      header: t("logs.columns.description"),
      accessor: (row) => {
        const desc = buildLogDescription(row, t);
        return (
          <span className={styles.cellDescription} title={desc}>
            {desc}
          </span>
        );
      },
      className: "logs-description-column",
    },
    {
      header: t("logs.columns.status"),
      accessor: (row) => {
        const chip = getStatusChip(row, t);
        return (
          <Chip type={chip.type} variant="outlined" size="small">
            {chip.label}
          </Chip>
        );
      },
      className: "logs-status-column",
    },
  ];

  const renderActions = (row: AuditLog) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] ?? false;
    const menuContent = (
      <div className={styles.actionMenu}>
        <button
          className={styles.actionMenuItem}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLogId(row.id);
            setIsDetailDrawerOpen(true);
            handleDropdownToggle(rowId, false);
          }}
        >
          <EyeIcon width={20} height={20} />
          {t("logs.actions.view")}
        </button>
      </div>
    );
    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  const selectedSortValue =
    sortOrder === "desc" ? `${sortBy}-desc` : `${sortBy}-asc`;
  const sortOptions: SortOption[] = sortByOptions.map((opt) => ({
    ...opt,
    value: `${opt.value}-${opt.direction ?? "desc"}`,
  }));

  // Anasayfadaki InfoCard stili — her stat'a tema rengi icon + value, tıklama
  // status filtresini toggle eder. Active state için SCSS'te tone bazlı border
  // override'ı kullanıyoruz (InfoCard varsayılan border'ını kapatıp vurgu veriyor).
  const statCards: Array<{
    key: "all" | "SUCCESS" | "FAILED" | "ERROR";
    label: string;
    value: number;
    tone: "neutral" | "success" | "warning" | "error";
    iconBg: string;
    icon: React.ReactNode;
  }> = [
    {
      key: "all",
      label: t("logs.stats.total"),
      value: statusCounts.total,
      tone: "neutral",
      iconBg: "#14181f",
      icon: <LogsIcon width={20} height={20} color="#fff" />,
    },
    {
      key: "SUCCESS",
      label: t("logs.stats.success"),
      value: statusCounts.success,
      tone: "success",
      iconBg: "#16a34a",
      icon: <SuccessIcon width={20} height={20} color="#fff" />,
    },
    {
      key: "FAILED",
      label: t("logs.stats.failed"),
      value: statusCounts.failed,
      tone: "warning",
      iconBg: "#d97706",
      icon: <AlertIcon width={20} height={20} color="#fff" />,
    },
    {
      key: "ERROR",
      label: t("logs.stats.error"),
      value: statusCounts.error,
      tone: "error",
      iconBg: "#e7212b",
      icon: <ErrorIcon width={20} height={20} color="#fff" />,
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("logs.title")} />

      {/* Anasayfa stilinde InfoCard'lar — renkli icon + değer + başlık. Tıklama
          status filtresini toggle eder. Aktif kart tone'una göre border +
          shadow vurgusu alır (tone className'i ile global override).
          NOT: PageHeader zaten kendi margin-bottom: 24px'ine sahip; ayrıca
          wrapper ile sarmalamıyoruz — diğer admin sayfalarıyla birebir aynı
          ritim. statCards kendi margin-bottom'u ile sıradaki PageHeader'a
          24px boşluk bırakıyor. */}
      <div className={styles.statCards}>
        {statCards.map((card) => {
          const isActive =
            card.key === "all" ? !activeStatus : activeStatus === card.key;
          const wrapperClass = [
            styles.statCardWrap,
            styles[`statCardWrap_${card.tone}`],
            isActive ? styles.statCardWrapActive : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <div key={card.key} className={wrapperClass}>
              <InfoCard
                title={card.label}
                value={card.value.toLocaleString("tr-TR")}
                icon={card.icon}
                iconBgColor={card.iconBg}
                onClick={() =>
                  handleStatusCardClick(
                    card.key === "all"
                      ? null
                      : (card.key as "SUCCESS" | "FAILED" | "ERROR"),
                  )
                }
              />
            </div>
          );
        })}
      </div>

      <PageHeader
        searchPlaceholder={t("logs.searchPlaceholder")}
        searchValue={searchInput}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        selectedSortValue={selectedSortValue}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={handleDownload}
        addButtonLabel={t("logs.downloadButton")}
        addButtonIcon={<DownloadIcon width={20} height={20} />}
        addButtonVariant="outlined"
        addButtonColor="error"
        addButtonLoading={isExporting}
      />

      <Table
        columns={columns}
        data={logs}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onRowClick={(row) => {
          setSelectedLogId(row.id);
          setIsDetailDrawerOpen(true);
        }}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("logs.noData")}
      />

      <LogDetailDrawer
        isOpen={isDetailDrawerOpen}
        onClose={() => {
          setIsDetailDrawerOpen(false);
          setSelectedLogId(null);
        }}
        logId={selectedLogId}
      />
    </DashboardLayout>
  );
};

export default LogsPage;
